/**
 * WHOSE QUEUE IS THIS — pure, zero imports, driven directly by
 * verify-reviews.mjs.
 *
 * Measured on the live app 2026-09-11: the Ask-today tab rendered **274 cards**
 * in one list, 76,906px of page, 95 phone-screens of scroll, with no search, no
 * filter and no pagination. Every rep saw every other rep's book. The fix is not
 * a smaller page size on the same list — it is answering "which of these is
 * MINE" first, because that is the only question a rep opens this screen with.
 *
 * TWO RULES GOVERN THIS FILE, and they pull in opposite directions:
 *
 *  1. Rep-scoped is the DEFAULT. Firm-wide is one tap away and never the
 *     landing.
 *  2. A scope that cannot resolve must SAY SO, never render as an empty queue.
 *     An empty list reads as "no work today" and a rep acts on it. That failure
 *     already has precedent in this repo — a rate-limited people lookup once
 *     rendered as "this school has nobody" on a school holding 45 contacts — so
 *     `resolved: false` is a distinct state here, and AskQueue renders it as an
 *     explanation with the firm-wide list one tap away.
 */

/** Fold a display name to comparable tokens. Never a substring match. */
export function nameTokens(value) {
  if (typeof value !== 'string') return []
  return value
    .toLowerCase()
    .replace(/[.,]/g, ' ')
    .split(/\s+/)
    .map((t) => t.trim())
    // Suffixes and initials carry no identity: "Philip Davanzo III" and
    // "Philip Davanzo" are one person, and "J" from "Sarah J Potter" must not
    // be the token that makes a match.
    .filter((t) => t.length > 1 && !SUFFIXES.has(t))
}

const SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'v'])

/**
 * The names the signed-in person could appear under on an order.
 * The user DTO is not pinned (auth.jsx falls back to `{ username }`), so every
 * plausible field is read and the caller is told whether anything usable came
 * back.
 */
export function identityOf(user) {
  const raw = [
    user?.name, user?.fullName, user?.full_name, user?.displayName,
    user?.display_name, user?.rep, user?.username,
  ].filter((v) => typeof v === 'string' && v.trim())

  const candidates = raw
    .map((v) => {
      // An email or a login handle is not a name. "brian.kass@sr.com" and
      // "brian.kass" both reduce to the two tokens that ARE the name.
      const base = v.includes('@') ? v.slice(0, v.indexOf('@')) : v
      return base.replace(/[._-]+/g, ' ')
    })
    .map((v) => nameTokens(v))
    .filter((toks) => toks.length >= 2)

  return {
    // A single-token identity ("representative", "admin") is NOT usable: it
    // would match every rep sharing a first name. Two tokens minimum.
    resolved: candidates.length > 0,
    candidates,
    label: raw[0] || null,
  }
}

/**
 * Does this order belong to this person?
 * Token containment both ways, so "Philip Davanzo III" on the order matches
 * "Philip Davanzo" on the account and vice versa — but "Philip" alone never
 * matches "Philip Davanzo", because a first name is not an identity.
 */
export function ownsRow(row, identity) {
  if (!identity?.resolved) return false
  const reps = Array.isArray(row?.reps) ? row.reps : []
  for (const rep of reps) {
    const repToks = nameTokens(rep)
    if (repToks.length < 2) continue
    for (const mine of identity.candidates) {
      const short = mine.length <= repToks.length ? mine : repToks
      const long = mine.length <= repToks.length ? repToks : mine
      if (short.length >= 2 && short.every((t) => long.includes(t))) return true
    }
  }
  return false
}

/**
 * Split the queue. `mine` is what lands; `firm` is always the complete list, so
 * the firm-wide tab is never a filtered-down view that could hide a row.
 *
 * `resolved` is false when we could not establish who is signed in, OR when we
 * could but they own nothing in today's queue AND the queue is not empty — the
 * second case is a real answer ("nothing of yours today") rather than a
 * failure, so it is reported separately as `mineEmpty`.
 */
export function scopeQueue(rows, identity) {
  const all = Array.isArray(rows) ? rows : []
  if (!identity?.resolved) {
    return { mine: [], firm: all, resolved: false, mineEmpty: false, identified: 0 }
  }
  const mine = all.filter((r) => ownsRow(r, identity))
  return {
    mine,
    firm: all,
    resolved: true,
    mineEmpty: mine.length === 0 && all.length > 0,
    identified: mine.length,
  }
}

/* ---- paging ---------------------------------------------------------- */

/** One screenful of work, not a wall. */
export const PAGE = 20

/**
 * Never truncate silently — the receipt is part of the contract, exactly as it
 * is on the Pursuit Stages board. `shown`/`total` always reconcile.
 */
export function pageOf(rows, page = 1, size = PAGE) {
  const all = Array.isArray(rows) ? rows : []
  const capped = Math.max(1, page)
  const shown = all.slice(0, capped * size)
  return {
    rows: shown,
    shown: shown.length,
    total: all.length,
    remaining: Math.max(0, all.length - shown.length),
    hasMore: shown.length < all.length,
  }
}
